import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/keys';
import type { BackendObjectType } from '@/types/objectTypes';
import { useDemoMode, useDemoFilteredResult } from '@/features/demo/useDemoMode';
export interface XaviorObjectBase {
  id: string;
  type: BackendObjectType;
  createdAt: string;
  updatedAt: string;
  version: number;
  _etag?: string;
}

type ListResponse<T> = {
  items?: T[];
  nextCursor?: string;
};

/**
 * Build the exact React Query cache key for useObjectsByType.
 * Exported so that components doing manual setQueryData / invalidateQueries
 * always use a key that matches what the hook reads from.
 */
export function objectsByTypeQueryKey(
  type: BackendObjectType,
  params: { conversationId?: string; moduleId?: string; limit?: number; query?: string; startAfter?: string; startBefore?: string; expandRecurring?: boolean; includeDriveCache?: boolean } = {},
) {
  const { moduleId, limit = 50, query, startAfter, startBefore, expandRecurring, includeDriveCache } = params;
  return [...queryKeys.objects.byType(type), moduleId ? `mod:${moduleId}` : 'workspace', limit, query, startAfter, startBefore, expandRecurring === false ? 'noexpand' : undefined, includeDriveCache ? 'drivecache' : undefined].filter(Boolean);
}

export function useObjectsByType<T extends XaviorObjectBase>(
  type: BackendObjectType,
  params: { conversationId?: string; moduleId?: string; limit?: number; query?: string; includeArchived?: boolean; enabled?: boolean; startAfter?: string; startBefore?: string; expandRecurring?: boolean; includeDriveCache?: boolean } = {},
) {
  const { moduleId, limit = 50, query, includeArchived, enabled, startAfter, startBefore, expandRecurring, includeDriveCache } = params;
  const { filterItems } = useDemoMode();

  // #2725: Events refetch eagerly so calendar-sync updates surface quickly,
  // but we KEEP prior data on screen during refetches so the UI never flashes
  // a "Loading events..." placeholder. Disabling gcTime entirely (the previous
  // behavior) tore the cache down on every remount which both forced the
  // loading state to flash AND aggravated the stale-mapping reports in #1885
  // — without a cache there was no place for an invalidation to land, so the
  // next render briefly showed nothing.
  const isEvent = type === 'event';

  const result = useQuery({
    queryKey: [...objectsByTypeQueryKey(type, params), includeArchived ? 'archived' : 'active'],
    enabled: enabled !== false,
    // Stale-while-revalidate: keep previous data on screen during refetches so
    // the calendar/list never flashes an empty "Loading events..." state.
    placeholderData: (previousData) => previousData,
    staleTime: isEvent ? 0 : undefined,
    // Keep events cached for 5 minutes so re-mounts don't tear the list down.
    gcTime: isEvent ? 5 * 60_000 : undefined,
    refetchOnMount: isEvent ? 'always' : true,
    queryFn: async () => {
      const search = new URLSearchParams();
      search.set('limit', String(limit));
      if (moduleId) {
        search.set('moduleIds', moduleId);
      }
      if (query) search.set('query', query);
      if (includeArchived) search.set('includeArchived', 'true');
      // Surface Drive/OneDrive-cached rows (Picker-ingested files) that the
      // server excludes from /notes,/assets by default. Drive module opts in.
      if (includeDriveCache) search.set('includeDriveCache', 'true');
      if (startAfter) search.set('startAfter', startAfter);
      if (startBefore) search.set('startBefore', startBefore);
      // Opt out of server-side recurrence expansion — this consumer expands
      // client-side (with exception handling). Default stays server-expanded.
      if (expandRecurring === false) search.set('expandRecurring', 'false');

      const { data } = await apiGet<ListResponse<T>>(`/${type}s?${search.toString()}`);
      const items = (data.items ?? []).map(item => {
        // Preserve _etag if it exists in the item data, otherwise keep version
        const preservedEtag = (item as any)._etag;
        const rawConversationLinks =
          (item as any).conversationLinks
          || (item as any).conversation_links
          || (item as any).data?.conversationLinks
          || (item as any).attributes?.conversationLinks
          || [];
        const normalizedConversationLinks = Array.isArray(rawConversationLinks)
          ? rawConversationLinks
            .map((entry: any) => {
              if (typeof entry === 'string') return { conversationId: entry };
              if (entry && typeof entry === 'object') {
                return {
                  conversationId:
                    entry.conversationId
                    || entry.conversation_id
                    || entry.id,
                };
              }
              return null;
            })
            .filter((entry: any) => Boolean(entry?.conversationId))
          : [];

        const rawModuleLinks =
          (item as any).moduleLinks
          || (item as any).module_links
          || (item as any).data?.moduleLinks
          || (item as any).attributes?.moduleLinks
          || [];
        const normalizedModuleLinks = Array.isArray(rawModuleLinks)
          ? rawModuleLinks
            .map((entry: any) => {
              if (typeof entry === 'string') return { moduleId: entry };
              if (entry && typeof entry === 'object') {
                return {
                  moduleId: entry.moduleId || entry.module_id || entry.id,
                };
              }
              return null;
            })
            .filter((entry: any) => Boolean(entry?.moduleId))
          : [];

        const normalizedConversationIds = Array.from(new Set(
          [
            (item as any).conversationId,
            ...((item as any).conversationIds || []),
            ...((item as any).conversation_ids || []),
            ...normalizedConversationLinks.map((entry: any) => entry.conversationId),
          ]
            .map((entry) => String(entry ?? '').trim())
            .filter(Boolean),
        ));

        const normalizedModuleIds = Array.from(new Set(
          [
            (item as any).moduleId,
            ...((item as any).moduleIds || []),
            ...((item as any).module_ids || []),
            ...((item as any).data?.moduleIds || []),
            ...((item as any).data?.module_ids || []),
            ...((item as any).attributes?.moduleIds || []),
            ...((item as any).attributes?.module_ids || []),
            ...normalizedModuleLinks.map((entry: any) => entry.moduleId),
          ]
            .map((entry) => String(entry ?? '').trim())
            .filter(Boolean),
        ));

        return {
          ...item,
          type,
          // Drive-sourced images carry only a Google `thumbnailLink` (CDN URL),
          // never a `thumbnailUrl`/`storageUrl`. The gallery + overlay read
          // `thumbnailUrl`, so surface the Drive thumbnail there (preserving any
          // real thumbnailUrl). Without this, Drive-sourced photos have no URL
          // for the gallery to render and show blank.
          thumbnailUrl:
            (item as any).thumbnailUrl
            ?? (item as any).thumbnailLink
            ?? (item as any).attributes?.thumbnailUrl
            ?? (item as any).attributes?.thumbnailLink,
          createdAt: (item as any).createdAt ?? (item as any).created_at,
          updatedAt: (item as any).updatedAt ?? (item as any).updated_at,
          conversationLinks: normalizedConversationLinks,
          conversation_links: normalizedConversationLinks,
          conversationIds: normalizedConversationIds,
          conversation_ids: normalizedConversationIds,
          moduleLinks: normalizedModuleLinks,
          module_links: normalizedModuleLinks,
          moduleIds: normalizedModuleIds,
          module_ids: normalizedModuleIds,
          // Only use item-specific ETag if present - do NOT use list ETag
          ...(preservedEtag && { _etag: preservedEtag }),
        };
      });
      return { ...data, items };
    },
  });

  return useDemoFilteredResult(result, filterItems);
}

export function useCreateObject<TPayload extends Record<string, any>, TObject extends XaviorObjectBase>(
  type: BackendObjectType,
  conversationId?: string,
  moduleId?: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TPayload & {
      conversationLinks?: { conversationId: string }[]
    }) => {
      const { data, etag } = await apiPost<TObject>(`/${type}s`, payload);
      return { ...data, _etag: etag };
    },
    onSuccess: () => {
      // Invalidate to refetch fresh data from server
      qc.invalidateQueries({ queryKey: queryKeys.objects.byType(type) });
      qc.invalidateQueries({ queryKey: queryKeys.objects.all });
    },
  });
}

/**
 * Retry the failed outbound calendar push for a single event (#3951).
 *
 * POSTs /events/:id/retry-sync, which re-runs syncEventV2 server-side and
 * returns the re-stamped event (providerSyncState 'synced' on success, or
 * 'error' again with an updated providerSyncError). Invalidates the event lists
 * + the detail so the "not synced" badge reflects the new state. The response
 * already carries providerSyncState/providerSyncError at the top level
 * (mapObject spreads the event's attributes), so no extra transform is needed.
 */
export function useRetryEventSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string }) => {
      const { data, etag } = await apiPost<XaviorObjectBase & { providerSyncState?: string; providerSyncError?: string }>(
        `/events/${input.id}/retry-sync`,
        {},
      );
      return { ...data, _etag: etag };
    },
    onSuccess: (_data, input) => {
      qc.invalidateQueries({ queryKey: queryKeys.objects.byType('event') });
      qc.invalidateQueries({ queryKey: queryKeys.objects.all });
      qc.invalidateQueries({ queryKey: queryKeys.objects.detail(input.id) });
    },
  });
}

export function useUpdateObject<TObject extends XaviorObjectBase>(type: BackendObjectType, conversationId?: string, moduleId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; etag: string; patch: Partial<TObject> }) => {
      const { id, etag, patch } = input;
      const { data, etag: newEtag } = await apiPatch<TObject>(`/${type}s/${id}`, patch, etag);
      return { ...data, _etag: newEtag ?? etag };
    },
    // Optimistic update: immediately update the UI before the API call completes
    // This prevents "snap-back" when dragging kanban cards between columns
    onMutate: async (input) => {
      const { id, patch } = input;

      // Use module-scoped or conversation-scoped key for optimistic updates
      const scopeKey = moduleId || conversationId;
      if (!scopeKey) return {};

      // Build the exact query key including all params that useObjectsByType might use
      const baseKey = queryKeys.objects.byType(type);

      // Cancel any outgoing refetches to prevent them from overwriting our optimistic update
      await qc.cancelQueries({ queryKey: baseKey });

      // Snapshot all matching queries
      const previousQueries: Array<{ key: any; data: any }> = [];

      // Optimistically update all matching queries
      qc.getQueryCache().findAll({ queryKey: baseKey }).forEach((query) => {
        const oldData = query.state.data as { items?: TObject[] } | undefined;
        if (oldData) {
          previousQueries.push({ key: query.queryKey, data: oldData });
          qc.setQueryData<{ items?: TObject[] }>(query.queryKey, {
            ...oldData,
            items: (oldData.items || []).map((item) =>
              item.id === id ? { ...item, ...patch } : item
            ),
          });
        }
      });

      // Return context with previous data for rollback on error
      return { previousQueries, baseKey };
    },
    onError: (_err, _input, context) => {
      // Rollback by restoring previous data
      if (context?.previousQueries) {
        context.previousQueries.forEach(({ key, data }) => {
          qc.setQueryData(key, data);
        });
      }
    },
    onSuccess: () => {
      // Invalidate to refetch fresh data from server
      qc.invalidateQueries({ queryKey: queryKeys.objects.byType(type) });
      qc.invalidateQueries({ queryKey: queryKeys.objects.all });
    },
    onSettled: (_data, _error, input) => {
      // Invalidate detail query to ensure consistency
      qc.invalidateQueries({ queryKey: queryKeys.objects.detail(input.id) });
    },
  });
}
